import SocialLogin from '@forwardflow/components/Signup/SocialLogin'
import AuthService from '@forwardflow/services/AuthService'
import { DemoService } from '@forwardflow/services/DemoBookingService'
import LocalStorage from '@forwardflow/services/LocalStorage'
import { Button, TextField } from '@forwardflow/ui'
import { Button as Buttonv2 } from '@forwardflow/ui-v2'
import { Form, message } from 'antd'
import { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'

interface SignupFormProps {
   setShowForm: (show: boolean) => void
   showForm: boolean
   onSubmit: (submitted: boolean, email?: string) => void
   initialEmail?: string
   firstName?: string
   lastName?: string
}

export const SignupForm: React.FC<SignupFormProps> = ({
   setShowForm,
   showForm,
   onSubmit,
   initialEmail,
   firstName,
   lastName,
}) => {
   const [isLoading, setLoading] = useState<boolean>(false)
   const [formError, setFormError] = useState<string | null>(null)
   const [form] = Form.useForm()
   const [searchParams] = useSearchParams()
   const invitationToken = searchParams.get('invitationToken')

   const demoBookingEmail = searchParams.get('email')
   const demoBookingId = searchParams.get('demoBookingId')

   const clearSessionData = () => {
      const isTourDone = LocalStorage.get('isTourDone')
      const isCampaignTourDone = LocalStorage.get('isCampaignTourDone')

      LocalStorage.clear()

      if (isTourDone) LocalStorage.put('isTourDone', isTourDone)
      if (isCampaignTourDone)
         LocalStorage.put('isCampaignTourDone', isCampaignTourDone)
   }

   const handleSubmit = async () => {
      try {
         setFormError(null)
         setLoading(true)
         const values = await form.validateFields()
         if (invitationToken) {
            values.invitationToken = invitationToken
         }
         const data = await AuthService.apiSignup(
            values,
            window.location.origin
         )
         if (data.accessToken) {
            onSubmit(true, values.email)
         }

         if (demoBookingEmail && demoBookingId) {
            if (values.email !== demoBookingEmail) {
               clearSessionData()
               await DemoService.overrideDemoBookingInfo(
                  parseInt(demoBookingId),
                  { email: values.email || undefined }
               )
               message.success('Email has been replaced with demo email')
            }
         }
      } catch (error) {
         console.error('error', error)
         if (
            typeof error === 'object' &&
            error !== null &&
            'response' in error
         ) {
            const apiError = error as { response: { data: any } }
            const errorData = apiError.response.data

            if (!errorData.success) {
               const errorMessage =
                  errorData.errorMessages && errorData.errorMessages.length > 0
                     ? errorData.errorMessages[0]
                     : errorData.message || 'An error occurred during signup'

               setFormError(errorMessage)
               message.error(errorMessage)
            }
         } else {
            setFormError('An unexpected error occurred. Please try again.')
            message.error('An unexpected error occurred. Please try again.')
         }
         onSubmit(false)
      } finally {
         setLoading(false)
      }
   }

   useEffect(() => {
      if (initialEmail) {
         form.setFieldsValue({
            firstName: firstName,
            lastName: lastName,
            email: initialEmail,
         })
      }
   }, [initialEmail, form])

   if (!showForm) {
      return (
         <div className="w-full flex flex-col gap-4">
            <SocialLogin />

            <div className="flex items-center gap-4 my-2">
               <div className="flex-1 h-px bg-gray-700"></div>
               <span className="text-sm text-gray-400">OR</span>
               <div className="flex-1 h-px bg-gray-700"></div>
            </div>

            <Buttonv2
               variant="secondary"
               onClick={() => setShowForm(true)}
               className="w-full h-[60px]"
            >
               <span>Signup with Email and Password</span>
            </Buttonv2>
         </div>
      )
   }

   return (
      <>
         <Form
            layout="vertical"
            form={form}
            initialValues={{}}
            onFinish={handleSubmit}
            className="w-full text-left text-white"
            autoComplete="off"
         >
            <div className="flex flex-col gap-x-4 md:flex-row">
               <TextField
                  name="firstName"
                  label="First name"
                  formItemClassName="w-full ff-white-label"
                  placeholder="Enter your first name"
                  isRequired
                  errorMessage="Please enter your first name"
               />

               <TextField
                  name="lastName"
                  label="Last name"
                  formItemClassName="w-full ff-white-label"
                  placeholder="Enter your last name"
                  isRequired
                  errorMessage="Please enter your last name"
               />
            </div>

            <TextField
               name="email"
               label="Email"
               formItemClassName="w-full ff-white-label"
               placeholder="Enter your email"
               isRequired
               validateEmail
               errorMessage="Please enter a valid email"
            />

            <TextField
               tooltip="Password must be at least 8 characters long and contain at least one uppercase letter, one lowercase letter, one number, and one special character"
               name="password"
               type="password"
               label="Password"
               placeholder="Enter your password"
               formItemClassName="w-full ff-white-label"
               isRequired
               validatePassword
               errorMessage="Please enter a valid password"
            />
            {formError && (
               <div className="p-4 mb-4 text-red-400 border rounded-lg bg-red-500/20 border-red-500/50">
                  {formError}
               </div>
            )}
            <Button
               isLoading={isLoading}
               type="submit"
               className="mb-10 !mt-7 h-[60px]"
            >
               Create account
            </Button>
         </Form>
      </>
   )
}
